/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  Group,
  ParametersType
} from '../constants/telosair-interfaces';
import Button from '../controls/Button';
import { useUnifiedData } from '../hooks/useUnifiedData';
import useStorageState from '../hooks/useStorageState';
import { Filters } from '../services/localstorage.service';
import ParameterLegend from './ParameterLegend';
import DetailedFloor from './DetailedFloor';
import GroupMap from './MapGroup';
import Loader from './loader';

type FloorMapCarouselProps = {
  groups: Group[];
  loading: boolean;
  dataFilter: DashboardDataFilter | null;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
  /** Called when a device icon on an indoor floor plan is clicked. */
  onDeviceClick?: (device: DeviceAttributesV1) => void;
};

/**
 * Displays a carousel of floor maps.
 * @param props - FloorMapCarousel Properties.
 * @returns A React element representing the carousel.
 */
export default function FloorMapCarousel(props: FloorMapCarouselProps): ReactElement {
  const { groups, loading, dataFilter, setDataFilter, onDeviceClick } = props;
  const { parameters } = useUnifiedData();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedParameters, setSelectedParameters] = useStorageState<ParametersType[]>(
    Filters.dashboardFlooMapParameters,
    []
  );

  const selectedDevice = dataFilter?.type === 'device' ? dataFilter.id : null;

  /**
   * Handles changes in dropdown input values to select a parameter.
   * @param selectedValue - The selected value from the parameter input.
   */
  const handleOptionsSelectTemperature = (selectedValue: string): void => {
    const isSelected = selectedParameters.includes(selectedValue as ParametersType);
    if (isSelected) {
      setSelectedParameters((prev) => prev.filter((item) => item !== selectedValue));
      return;
    }
    setSelectedParameters((prev) => [...prev, selectedValue as ParametersType]);
  };

  /**
   * Gets the CSS class for a parameter based on its active state.
   * @param value - The parameter value.
   * @returns The CSS class string.
   */
  const parameterClass = (value: ParametersType): string => {
    return selectedParameters.includes(value) ? 'text-white' : 'text-gray-400';
  };

  /**
   * Handles the previous button press in the carousel.
   */
  const onPrevPress = (): void => {
    setActiveIndex((prev) => (prev === 0 ? groups.length - 1 : prev - 1));
  };

  /**
   * Handles the next button press in the carousel.
   */
  const onNextPress = (): void => {
    setActiveIndex((prev) => (prev === groups.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (dataFilter?.type === 'group') {
      groups?.forEach((group, index) => {
        if (dataFilter.id && group.groupId === Number(dataFilter.id)) {
          setActiveIndex(index);
        }
      });
    } else if (dataFilter?.type === 'device') {
      groups?.forEach((group, index) => {
        group.sensorDetailsList.forEach((sensor) => {
          if (Number(sensor.serial_number) === dataFilter.id) {
            setActiveIndex(index);
          }
        });
      });
    } else {
      setActiveIndex(0);
    }
  }, [dataFilter, groups]);

  return (
    <div className="h-full w-full flex flex-col relative">
      {loading && <Loader />}
      {!loading && groups?.length === 0 ? (
        <div className="flex flex-1 h-full flex-col justify-center items-center">
          <div className="text-gray-400 text-sm">
            There are no groups. Add groups by clicking below
          </div>
          <NavLink to="/settings/device-setup?tab=groups">
            <Button theme="primary" size="xs" className="mt-3" name="Add Groups" />
          </NavLink>
        </div>
      ) : (
        <div className="h-full flex flex-col flex-1 relative">
          <div
            className={`flex absolute top-0 ${
              showOverlay
                ? 'left-0 bg-gradient-to-b from-container-900/80 via-container-900/60 to-container-900/0'
                : ''
            } right-0 z-[1] p-2 gap-1 justify-between`}
          >
            {showOverlay && (
              <div className="flex flex-wrap gap-3">
                <ParameterLegend
                  handleParameterClick={() => setSelectedParameters([])}
                  name="All"
                  parameter={'all' as ParametersType}
                  active={selectedParameters.length === 0}
                  className={parameterClass('all' as ParametersType)}
                />
                {parameters.map((parameter) => (
                  <ParameterLegend
                    handleParameterClick={handleOptionsSelectTemperature}
                    name={parameter.shortName}
                    parameter={parameter.parameterName}
                    active={selectedParameters.includes(parameter.parameterName)}
                    key={parameter.parameterName}
                    className={parameterClass(parameter.parameterName)}
                  />
                ))}
              </div>
            )}
            <button
              onClick={() => setShowOverlay((prev) => !prev)}
              type="button"
              className={`material-icons-outlined text-gray-300 z-10 text-lg bg-gray-700 rounded-full min-h-[1.5rem] max-h-[1.5rem] min-w-[1.5rem] max-w-[1.5rem] flex items-center justify-center ${
                !showOverlay ? 'transform rotate-45' : ''
              }`}
            >
              close
            </button>
          </div>
          <div className="flex flex-1 relative">
            {showOverlay && (
              <button
                onClick={onPrevPress}
                type="button"
                className="material-icons-outlined text-gray-300 absolute left-5 top-1/2 transform -translate-y-1/2 z-10 text-3xl bg-gray-700 rounded-full h-10 w-10 flex items-center justify-center"
              >
                navigate_before
              </button>
            )}

            {groups?.map((item, index) => (
              <div
                key={item.groupId}
                style={{ display: index === activeIndex ? 'flex' : 'none' }}
                className="absolute inset-0 bg-container-600 border border-gray-400 rounded flex-1 justify-between gap-3 flex-col"
              >
                {activeIndex === index &&
                  (item.floorPlan === DeviceType.OUTDOOR ? (
                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                      <GroupMap
                        selectedParameters={selectedParameters}
                        selectedDevice={selectedDevice}
                        selectedGroup={item}
                        setDataFilter={setDataFilter}
                      />
                    </APIProvider>
                  ) : (
                    <DetailedFloor
                      selectedParameters={selectedParameters}
                      selectedDevice={selectedDevice}
                      setDataFilter={setDataFilter}
                      onDeviceClick={onDeviceClick}
                      onDashboard
                      showExpandedView={false}
                      selectedGroup={item}
                    />
                  ))}
                <div className="text-gray-300 text-xs font-medium text-center">
                  {item.groupName}{' '}
                  <span className="text-gray-400">
                    {item.sensorDetailsList?.length || 0} Devices
                  </span>
                </div>
              </div>
            ))}

            {showOverlay && (
              <button
                onClick={onNextPress}
                type="button"
                className="material-icons-outlined text-gray-300 absolute right-5 top-1/2 transform -translate-y-1/2 z-10 text-3xl bg-gray-700 rounded-full h-10 w-10 flex items-center justify-center"
              >
                navigate_next
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
